import React, { useState, useEffect } from 'react'
import Sidebar from '../../layouts/Sidebar'
import Topbar from '../../layouts/Topbar'
import api from '../../config/api'

const Settings = () => {
    // Tab state
    const [activeTab, setActiveTab] = useState('profile'); // 'profile' | 'company' | 'timeoff'

    // User profile state
    const [user, setUser] = useState({
        first_name: '',
        last_name: '',
        email: '',
        phone: '',
        address: ''
    });

    // Company profile state
    const [company, setCompany] = useState({
        id: null,
        name: '',
        email: '',
        phone: '',
        address: '',
        logo: null
    });

    // Logo upload
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');

    const [loading, setLoading] = useState(true);
    const [message, setMessage] = useState({ type: '', text: '' });

    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // Fetch User
                const userRes = await api.get('/auth/me/');
                setUser({
                    first_name: userRes.data.first_name || '',
                    last_name: userRes.data.last_name || '',
                    email: userRes.data.email || '',
                    phone: userRes.data.phone || '',
                    address: userRes.data.address || ''
                });

                // Fetch Company
                const companyRes = await api.get('/auth/company/');
                setCompany({
                    id: companyRes.data.id || null,
                    name: companyRes.data.name || '',
                    email: companyRes.data.email || '',
                    phone: companyRes.data.phone || '',
                    address: companyRes.data.address || '',
                    logo: companyRes.data.logo || null
                });

                if (companyRes.data.logo) {
                    setLogoPreview(companyRes.data.logo);
                }
            } catch (err) {
                console.error(err);
                setMessage({ type: 'danger', text: 'Error loading settings data' });
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    const handleUserChange = (e) => {
        const { name, value } = e.target;
        setUser(prev => ({ ...prev, [name]: value }));
    };

    const handleCompanyChange = (e) => {
        const { name, value } = e.target;
        setCompany(prev => ({ ...prev, [name]: value }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setLogoFile(file);
            setLogoPreview(URL.createObjectURL(file));
        }
    };

    const handleUserSubmit = async (e) => {
        e.preventDefault();
        try {
            setMessage({ type: '', text: '' });
            const res = await api.patch('/auth/me/', user);
            setUser({
                first_name: res.data.first_name || '',
                last_name: res.data.last_name || '',
                email: res.data.email || '',
                phone: res.data.phone || '',
                address: res.data.address || ''
            });
            setMessage({ type: 'success', text: 'Profile updated successfully!' });
        } catch (err) {
            console.error(err);
            setMessage({ type: 'danger', text: 'Failed to update profile' });
        }
    };

    const handleCompanySubmit = async (e) => {
        e.preventDefault();
        if (!company.id) {
            setMessage({ type: 'danger', text: 'Company ID not found' });
            return;
        }
        try {
            setMessage({ type: '', text: '' });
            const formData = new FormData();
            formData.append('name', company.name);
            formData.append('email', company.email);
            formData.append('phone', company.phone);
            formData.append('address', company.address);
            if (logoFile) {
                formData.append('logo', logoFile);
            }

            const res = await api.patch(`/auth/company/${company.id}/`, formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });

            setCompany({
                id: res.data.id || null,
                name: res.data.name || '',
                email: res.data.email || '',
                phone: res.data.phone || '',
                address: res.data.address || '',
                logo: res.data.logo || null
            });

            if (res.data.logo) {
                setLogoPreview(res.data.logo);
            }
            setLogoFile(null);
            setMessage({ type: 'success', text: 'Company details updated successfully!' });
        } catch (err) {
            console.error(err);
            setMessage({ type: 'danger', text: 'Failed to update company details' });
        }
    };

    const getLogoUrl = (logoPath) => {
        if (!logoPath) return 'assets/img/icons/company-logo.svg';
        if (logoPath.startsWith('http') || logoPath.startsWith('blob:')) return logoPath;
        return `http://127.0.0.1:8000${logoPath}`;
    };

    if (loading) {
        return (
            <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
                <Topbar />
                <Sidebar />
                <div className="page-wrapper d-flex align-items-center justify-content-center" style={{ minHeight: '100vh' }}>
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div data-sidebar-skin="dark" data-header-skin="light" data-navbar-brand-skin="dark" data-sidebar-state="expand">
            <Topbar />
            <Sidebar />
            <div className="page-wrapper">

                { /* Start Content */ }
                <div className="content">

                    { /* Page Header */ }
                    <div className="d-flex align-items-center justify-content-between flex-wrap row-gap-3 mb-3">
                        <div className="flex-grow-1">
                            <h5 className="fw-bold">Settings</h5>
                        </div>
                    </div>
                    { /* End Page Header */ }

                    {message.text && (
                        <div className={`alert alert-${message.type} alert-dismissible fade show`} role="alert">
                            {message.text}
                            <button type="button" className="btn-close" onClick={() => setMessage({ type: '', text: '' })} aria-label="Close"></button>
                        </div>
                    )}

                    <div className="d-flex align-items-center flex-wrap nav-tab-dark row-gap-2 mb-3" role="tablist">
                        <button
                            type="button"
                            onClick={() => setActiveTab('profile')}
                            className={`btn btn-sm border fs-14 me-2 ${activeTab === 'profile' ? 'btn-primary text-white' : 'btn-light'}`}
                        >
                            Profile Settings
                        </button>
                        <button
                            type="button"
                            onClick={() => setActiveTab('company')}
                            className={`btn btn-sm border fs-14 me-2 ${activeTab === 'company' ? 'btn-primary text-white' : 'btn-light'}`}
                        >
                            Company Settings
                        </button>
                        
                    </div>

                    <div className="tab-content">
                        {activeTab === 'profile' && (
                            <div className="tab-pane show active" role="tabpanel">
                                { /* start row */ }
                                <div className="row">
                                    <div className="col-lg-12">
                                        <div className="card rounded-0 mb-0">
                                            <div className="card-header">
                                                <h6 className="fw-bold mb-0">Basic Information</h6>
                                            </div> { /* end card header*/ }
                                            <form onSubmit={handleUserSubmit}>
                                                <div className="card-body">
                                                    { /* start row */ }
                                                    <div className="row row-gap-3">

                                                        <div className="col-lg-6">
                                                            <div className="mb-0">
                                                                <label className="form-label">First Name<span className="text-danger ms-1">*</span></label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="first_name"
                                                                    value={user.first_name}
                                                                    onChange={handleUserChange}
                                                                    required
                                                                />
                                                            </div>
                                                        </div>{ /* end col */ }

                                                        <div className="col-lg-6">
                                                            <div className="mb-0">
                                                                <label className="form-label">Last Name<span className="text-danger ms-1">*</span></label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="last_name"
                                                                    value={user.last_name}
                                                                    onChange={handleUserChange}
                                                                    required
                                                                />
                                                            </div>
                                                        </div>{ /* end col */ }

                                                        <div className="col-lg-6">
                                                            <div className="mb-0">
                                                                <label className="form-label">Email Address<span className="text-danger ms-1">*</span></label>
                                                                <input
                                                                    type="email"
                                                                    className="form-control"
                                                                    name="email"
                                                                    value={user.email}
                                                                    onChange={handleUserChange}
                                                                    required
                                                                />
                                                            </div>
                                                        </div>{ /* end col */ }

                                                        <div className="col-lg-6">
                                                            <div className="mb-0">
                                                                <label className="form-label">Phone Number</label>
                                                                <input
                                                                    type="text"
                                                                    className="form-control"
                                                                    name="phone"
                                                                    value={user.phone}
                                                                    onChange={handleUserChange}
                                                                />
                                                            </div>
                                                        </div>{ /* end col */ }

                                                        <div className="col-lg-12">
                                                            <div className="mb-0">
                                                                <label className="form-label">Address</label>
                                                                <textarea
                                                                    className="form-control"
                                                                    name="address"
                                                                    rows="3"
                                                                    value={user.address}
                                                                    onChange={handleUserChange}
                                                                />
                                                            </div>
                                                        </div>{ /* end col */ }

                                                    </div>
                                                    { /* end row */ }

                                                </div> { /* end card body */ }
                                                <div className="card-footer">
                                                    <div className="d-flex align-items-center justify-content-end">
                                                        <button type="submit" className="btn btn-primary">Save Changes</button>
                                                    </div>
                                                </div>
                                            </form>
                                        </div> { /* end card */ }
                                    </div> { /* end col */ }
                                </div>
                                { /* end row */ }
                            </div>
                        )}

                        {activeTab === 'company' && (
                            <div className="tab-pane show active" role="tabpanel">
                                <div className="card rounded-0 mb-0">
                                    <div className="card-header">
                                        <h6 className="fw-bold mb-0">Company Info</h6>
                                    </div> { /* end card header */ }
                                    <form onSubmit={handleCompanySubmit}>
                                        <div className="card-body">
                                            <div className="d-flex align-items-center mb-3">
                                                <div className="avatar avatar-xxl border border-dashed position-relative me-3 flex-shrink-0 p-2 d-flex align-items-center justify-content-center">
                                                    <img src={`https:/` + company.logo} className="img-fluid" style={{ maxHeight: '100%', maxWidth: '100%', objectFit: 'contain' }} alt="Company Logo" />
                                                </div>
                                                <div className="d-inline-flex flex-column align-items-start">
                                                    <div className="drag-upload-btn btn btn-sm btn-primary position-relative mb-2">
                                                        <i className="ti ti-photo me-1" />Change Image
                                                        <input type="file" className="form-control image-sign" onChange={handleLogoChange} accept="image/*" />
                                                    </div>
                                                    <span className="text-dark fs-12">JPG or PNG format, not exceeding 5MB.</span>
                                                </div>
                                            </div>

                                            { /* start row */ }
                                            <div className="row row-gap-3">

                                                <div className="col-lg-6">
                                                    <div className="mb-0">
                                                        <label className="form-label">Company Name<span className="text-danger ms-1">*</span></label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="name"
                                                            value={company.name}
                                                            onChange={handleCompanyChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>{ /* end col */ }

                                                <div className="col-lg-6">
                                                    <div className="mb-0">
                                                        <label className="form-label">Company Email<span className="text-danger ms-1">*</span></label>
                                                        <input
                                                            type="email"
                                                            className="form-control"
                                                            name="email"
                                                            value={company.email}
                                                            onChange={handleCompanyChange}
                                                            required
                                                        />
                                                    </div>
                                                </div>{ /* end col */ }

                                                <div className="col-lg-6">
                                                    <div className="mb-0">
                                                        <label className="form-label">Company Phone</label>
                                                        <input
                                                            type="text"
                                                            className="form-control"
                                                            name="phone"
                                                            value={company.phone}
                                                            onChange={handleCompanyChange}
                                                        />
                                                    </div>
                                                </div>{ /* end col */ }

                                                <div className="col-lg-12">
                                                    <div className="mb-0">
                                                        <label className="form-label">Company Address</label>
                                                        <textarea
                                                            className="form-control"
                                                            name="address"
                                                            rows="3"
                                                            value={company.address}
                                                            onChange={handleCompanyChange}
                                                        />
                                                    </div>
                                                </div>{ /* end col */ }

                                            </div>
                                            { /* end row */ }

                                        </div> { /* end card body */ }
                                        <div className="card-footer">
                                            <div className="d-flex align-items-center justify-content-end">
                                                <button type="submit" className="btn btn-primary">Save Changes</button>
                                            </div>
                                        </div> { /* end card footer */ }
                                    </form>
                                </div> { /* end card */ }
                            </div>
                        )}

                        {activeTab === 'timeoff' && (
                            <div className="tab-pane show active" role="tabpanel">
                                { /* start row */ }
                                <div className="row">

                                    <div className="col-xl-5">
                                        <div className="card rounded-0 mb-xl-0">
                                            <div className="card-header d-flex align-items-center justify-content-between">
                                                <h6 className="fw-bold mb-0">Basic Information</h6>
                                                <a href="#" className="btn p-1 border-0 btn-outline-white"><i className="ti ti-edit" /></a>
                                            </div> { /* end card header */ }
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-sm-6">
                                                        <div className="bg-light p-2 border rounded mb-3">
                                                            <h6 className="fw-semibold fs-14 mb-1">Allowance</h6>
                                                            <p className="mb-0 fs-13">25 Days</p>
                                                        </div>
                                                    </div>
                                                    <div className="col-sm-6">
                                                        <div className="bg-light p-2 border rounded mb-3">
                                                            <h6 className="fw-semibold fs-14 mb-1">Year Start</h6>
                                                            <p className="mb-0 fs-13">01 Jan 2025</p>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className="bg-light border rounded d-flex align-items-center justify-content-between p-2 mb-3">
                                                    <div className="d-flex align-items-center">
                                                        <span className="btn btn-icon btn-sm bg-white text-dark border flex-shrink-0 me-2"><i className="ti ti-smart-home" /></span>
                                                        <p className="mb-0 fs-13 text-dark">Working From Home</p>
                                                    </div>
                                                    <div className="form-check form-switch custom-form-switch">
                                                        <input className="form-check-input" type="checkbox" role="switch" defaultChecked={true} />
                                                    </div>
                                                </div>
                                                <div className="bg-light border rounded p-2">
                                                    <h6 className="fs-14 fw-semibold">Working Week</h6>
                                                   <div className="d-flex align-items-center flex-wrap gap-2">
                                                        <span className="badge badge-soft-success">Mon</span>
                                                        <span className="badge badge-soft-success">Tue</span>
                                                        <span className="badge badge-soft-success">Wed</span>
                                                        <span className="badge badge-soft-success">Thu</span>
                                                        <span className="badge badge-soft-success">Fri</span>
                                                        <span className="badge badge-soft-danger">Sat</span>
                                                        <span className="badge badge-soft-danger">Sun</span>
                                                    </div>
                                                </div>
                                            </div> { /* end card body */ }
                                        </div> { /* end card */ }
                                    </div> { /* end col */ }

                                    <div className="col-xl-7">
                                        <div className="card">
                                            <div className="card-body p-2">
                                                <div className="d-flex align-items-center justify-content-between mb-2">
                                                    <h6 className="mb-0 fw-bold">Working from Home</h6>
                                                    <div className="form-check form-switch custom-form-switch">
                                                        <input className="form-check-input" type="checkbox" role="switch" defaultChecked={true} />
                                                    </div>
                                                </div>
                                                <p className="mb-0">Reflect your company's working from home policy by editing the approval process or disabling the feature entirely.</p>
                                            </div>
                                        </div>
                                        <div className="card mb-0">
                                            <div className="card-header">
                                                <h6 className="mb-0 fw-bold">Focus Technologies</h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="table-responsive">
                                                    <table className="table table-nowrap border">
                                                        <thead className="table-light">
                                                            <tr>
                                                                <th>Team Member</th>
                                                                <th>Allowance</th>
                                                                <th>Day Used</th>
                                                                <th>Approver</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            <tr>
                                                                <td>
                                                                    <div className="d-flex align-items-center">
                                                                        <a href="#" className="avatar avatar-sm avatar-rounded flex-shrink-0 me-2"><img src="/assets/img/employees/employee-01.jpg" alt="" /></a>
                                                                        <h6 className="fs-14 mb-0"><a href="#">John Carter</a></h6>
                                                                    </div>
                                                                </td>
                                                                <td>25</td>
                                                                <td>20</td>
                                                                <td>
                                                                    <div className="d-flex align-items-center">
                                                                        <a href="#" className="avatar avatar-sm avatar-rounded flex-shrink-0 me-2"><img src="/assets/img/managers/manager-01.jpg" alt="" /></a>
                                                                        <h6 className="fs-14 mb-0"><a href="#">Braun Kelton</a></h6>
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        </div>
                                    </div> { /* end col */ }

                                </div>
                                { /* end row */ }
                            </div>
                        )}
                    </div>

                </div>
                { /* End Content */ }

            </div>
        </div>
    );
};

export default Settings

